import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useGetList, useNotify, useRecordContext, useRefresh, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Row, SectionCard } from '../../shared/Panel';
import { useFeature } from '../featureFlags/FeatureGate';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { FLEET_CORRECTIONS } from './fleetFeatures';
import {
  REASON_MAX,
  REASON_MIN,
  correctionFor,
  correctionTooltip,
  correctionsOf,
  fetchCorrectionContext,
  restoreFromTms,
  saveDriverCorrection,
  saveEligibilityCorrection,
  saveTrailerCorrection,
  savedMessage,
  termText,
  type CorrectionContext,
  type CorrectionResult,
  type CorrectionView,
} from './fleetCorrectionsApi';

/**
 * Korekty kartoteki pojazdu na karcie (22.10.2026, przełącznik
 * `fleetCorrections`, S-22.10-03) — makieta FTL p. Mariusza, procedura
 * FTL-08 (`editCar`, `editPermit`, `restoreTms`); decyzja użytkownika
 * 22.10.2026: „tak, według makiety”.
 *
 * - Sekcja „Kierowca i zestaw”: kierowca i naczepa z „Zmień”; wartość
 *   różna od TMS ma znacznik „≠ TMS” (dymek: kto, kiedy, powód, wartość
 *   w TMS) i „przywróć z TMS”; gdy TMS zmienił się od korekty inaczej —
 *   ostrzeżenie „TMS ma teraz …”.
 * - Okno zmiany kierowcy pokazuje skutki przed zapisem: bez kierowcy pojazd
 *   wypada z rekomendacji, a jego warianty wygasają (lista zleceń
 *   z autorami), oraz obsadzenia auta.
 * - „Zmień termin” przy uprawnieniach — `CarEligibilities.tsx`, to samo
 *   okno z datą (`EligibilityTermDialog`).
 *
 * Kto może: opiekun pojazdu (swoje auta) albo Team Leader i administratorzy
 * — rozstrzyga API (`GET …/corrections/context`); przycisk innych jest
 * wyszarzony z powodem w dymku, jak w makiecie (FTL-08, krok 7). Ukryty
 * czy wyszarzony przycisk nie jest uprawnieniem — zapis i tak sprawdza API.
 */

interface CorrectionsState {
  /** Przełącznik włączony dla konta. */
  enabled: boolean;
  carId: number | string | null;
  context: CorrectionContext | null;
  /** Odświeża rekord karty i kontekst po zapisie. */
  reload: () => void;
}

const EMPTY: CorrectionsState = { enabled: false, carId: null, context: null, reload: () => undefined };

const CorrectionsContext = createContext<CorrectionsState>(EMPTY);

export const useFleetCorrections = (): CorrectionsState => useContext(CorrectionsContext);

/** Dostawca kontekstu korekt — pod rekordem karty pojazdu. */
export const FleetCorrectionsProvider = ({ children }: { children: ReactNode }) => {
  const record = useRecordContext<RaRecord>();
  const enabled = useFeature(FLEET_CORRECTIONS);
  const refresh = useRefresh();
  const id = record?.id ?? null;
  const [context, setContext] = useState<CorrectionContext | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled || id === null) return undefined;
    let cancelled = false;
    fetchCorrectionContext(id)
      .then((c) => {
        if (!cancelled) setContext(c);
      })
      .catch(() => {
        // Bez kontekstu przyciski zostają wyszarzone — zapis i tak sprawdza API.
        if (!cancelled) setContext(null);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, id, tick]);

  const reload = useCallback(() => {
    refresh();
    setTick((t) => t + 1);
  }, [refresh]);
  const value = useMemo(() => ({ enabled, carId: id, context, reload }), [enabled, id, context, reload]);
  return <CorrectionsContext.Provider value={value}>{children}</CorrectionsContext.Provider>;
};

/** Powód, dla którego przycisk jest wyszarzony; `null` — wolno. */
function editRefusal(state: CorrectionsState): string | null {
  if (!state.context) return 'Sprawdzam, czy możesz korygować kartotekę tego pojazdu…';
  return state.context.access.allowed ? null : (state.context.access.refusal ?? 'Brak uprawnień do korekty.');
}

/** Przycisk akcji — wyszarzony z powodem w dymku (MUI: dymek na `span`). */
export const CorrectionButton = ({
  label,
  testId,
  refusal,
  onClick,
}: {
  label: string;
  testId: string;
  refusal: string | null;
  onClick: () => void;
}) => (
  <Tooltip title={refusal ?? ''} disableHoverListener={refusal === null}>
    <span>
      <Button size="small" variant="outlined" data-testid={testId} disabled={refusal !== null} onClick={onClick}>
        {label}
      </Button>
    </span>
  </Tooltip>
);

/**
 * Znacznik „≠ TMS” z dymkiem i „przywróć z TMS” — przy kierowcy, naczepie
 * i terminie. `testKey` — część identyfikatorów (`driver`, `trailer`, id
 * uprawnienia).
 */
export const CorrectionMark = ({
  correction,
  testKey,
  onRestored,
}: {
  correction: CorrectionView;
  testKey: string;
  onRestored?: () => void;
}) => {
  const state = useFleetCorrections();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const refusal = editRefusal(state);
  const restore = () =>
    void (async () => {
      if (state.carId === null) return;
      setBusy(true);
      try {
        await restoreFromTms(state.carId, correction.id);
        notify('Przywrócono wartość z TMS.', { type: 'success' });
        state.reload();
        onRestored?.();
      } catch (e) {
        notify(toErrorMessage(e), { type: 'error' });
      } finally {
        setBusy(false);
      }
    })();
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      <Tooltip
        title={
          <Box component="span" sx={{ display: 'block', whiteSpace: 'pre-line' }}>
            {correctionTooltip(correction).join('\n')}
          </Box>
        }
      >
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label="≠ TMS"
          data-testid={`car-correction-mark-${testKey}`}
          aria-label={correctionTooltip(correction).join('. ')}
        />
      </Tooltip>
      <Tooltip title={refusal ?? ''} disableHoverListener={refusal === null}>
        <span>
          <Button
            size="small"
            sx={{ minWidth: 0, px: 0.5, textTransform: 'none', whiteSpace: 'nowrap' }}
            data-testid={`car-correction-restore-${testKey}`}
            disabled={refusal !== null || busy}
            onClick={restore}
          >
            przywróć z TMS
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

/** Ostrzeżenie „TMS ma teraz …” pod wartością. */
const TmsChanged = ({ correction, testKey }: { correction: CorrectionView | null; testKey: string }) =>
  correction?.warning ? (
    <Typography
      variant="caption"
      color="warning.main"
      sx={{ display: 'block' }}
      data-testid={`car-correction-warning-${testKey}`}
    >
      {correction.warning}
    </Typography>
  ) : null;

// ── okno korekty ──────────────────────────────────────────────────────

/** Okno korekty: wstęp, pole wartości, powód, skutki, „Zapisz korektę”. */
const CorrectionDialog = ({
  open,
  title,
  intro,
  children,
  effects,
  okLabel,
  canSave,
  onClose,
  onSave,
}: {
  open: boolean;
  title: string;
  intro: string;
  children: ReactNode;
  effects: ReactNode;
  okLabel: string;
  canSave: boolean;
  onClose: () => void;
  onSave: (reason: string) => Promise<void>;
}) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);
  const tooShort = reason.trim().length < REASON_MIN;
  const save = () =>
    void (async () => {
      setBusy(true);
      setError(null);
      try {
        await onSave(reason);
      } catch (e) {
        setError(toErrorMessage(e));
      } finally {
        setBusy(false);
      }
    })();
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" data-testid="car-correction-dialog">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{intro}</DialogContentText>
        <Stack spacing={2}>
          {children}
          <TextField
            label="Powód korekty"
            multiline
            minRows={2}
            fullWidth
            size="small"
            value={reason}
            disabled={busy}
            onChange={(e) => setReason(e.target.value)}
            helperText={`Co najmniej ${REASON_MIN} znaków — zostaje w historii pojazdu.`}
            slotProps={{ htmlInput: { maxLength: REASON_MAX, 'data-testid': 'dlg-powod' } }}
          />
          <Box data-testid="car-correction-effects">{effects}</Box>
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button variant="contained" data-testid="dlg-ok" disabled={busy || tooShort || !canSave} onClick={save}>
          {okLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const listText = (items: string[]) => (items.length > 0 ? items.join(', ') : 'brak');

const MARKED = 'Wartość będzie oznaczona jako różna od TMS, z autorem i powodem.';

/** Kierowca z listy kartoteki — tyle, ile pokazuje okno. */
interface DriverOption {
  id: number;
  name?: string | null;
  surname?: string | null;
  tmsDriverId?: number | null;
  cars?: Array<{ registrationNumber?: string | null }>;
}

const personName = (d: { name?: unknown; surname?: unknown } | null | undefined): string =>
  d ? [d.name, d.surname].filter((x) => typeof x === 'string' && x.trim() !== '').join(' ') : '';

/** Wartość listy „bez kierowcy” — nie id kierowcy. */
const NO_DRIVER = 'none';

/** Okno „Zmień kierowcę” — z wyszukiwaniem w kartotece kierowców i skutkami. */
export const DriverCorrectionDialog = ({
  open,
  onClose,
  plate,
  current,
  tmsLabel,
}: {
  open: boolean;
  onClose: () => void;
  plate: string;
  /** Kierowca na karcie teraz (po korekcie albo z TMS). */
  current: { id: number; name?: unknown; surname?: unknown } | null;
  /** Kierowca w TMS — z korekty, a bez niej ten na karcie. */
  tmsLabel: string;
}) => {
  const state = useFleetCorrections();
  const notify = useNotify();
  const [q, setQ] = useState('');
  const [choice, setChoice] = useState('');
  useEffect(() => {
    if (open) {
      setQ('');
      setChoice('');
    }
  }, [open]);
  const { data = [] } = useGetList<DriverOption & RaRecord>(
    'drivers',
    {
      filter: { active: true, ...(q.trim() ? { q: q.trim() } : {}) },
      pagination: { page: 1, perPage: 20 },
      sort: { field: 'surname', order: 'ASC' },
    },
    { enabled: open },
  );
  const effects = state.context?.effects ?? { variants: [], assignments: [] };
  const save = async (reason: string) => {
    if (state.carId === null) return;
    const driverId = choice === NO_DRIVER ? null : Number(choice);
    const result: CorrectionResult = await saveDriverCorrection(state.carId, driverId, reason);
    notify(savedMessage(result, 'Zapisano korektę kartoteki'), { type: 'success' });
    onClose();
    state.reload();
  };
  return (
    <CorrectionDialog
      open={open}
      title={`Zmień kierowcę: ${plate}`}
      intro={`Obecnie: ${personName(current) || 'brak'}. W TMS: ${tmsLabel}.`}
      okLabel="Zapisz korektę"
      canSave={choice !== ''}
      onClose={onClose}
      onSave={save}
      effects={
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          <li>
            <Typography variant="body2">{MARKED}</Typography>
          </li>
          <li>
            <Typography variant="body2">
              Bez kierowcy pojazd wypada z rekomendacji, a jego warianty wygasają:{' '}
              {listText(effects.variants.map((v) => (v.author ? `${v.number} (${v.author})` : v.number)))}.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">Obsadzenia pojazdu: {listText(effects.assignments.map((a) => a.number))}.</Typography>
          </li>
        </Box>
      }
    >
      <TextField
        size="small"
        fullWidth
        label="Szukaj kierowcy (nazwisko, imię, nr karty)"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        slotProps={{ htmlInput: { 'data-testid': 'car-correction-driver-search' } }}
      />
      <TextField
        select
        size="small"
        fullWidth
        label="Kierowca"
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
        slotProps={{
          select: { native: true },
          inputLabel: { shrink: true },
          htmlInput: { 'data-testid': 'car-correction-driver-select' },
        }}
      >
        <option value="" disabled>
          — wybierz —
        </option>
        <option value={NO_DRIVER}>— bez kierowcy —</option>
        {data
          .filter((d) => d.id !== current?.id)
          .map((d) => {
            const plates = (d.cars ?? []).map((c) => c.registrationNumber).filter(Boolean);
            return (
              <option key={d.id} value={String(d.id)}>
                {personName(d) || `#${d.id}`}
                {plates.length > 0 ? ` (przy pojeździe ${plates.join(', ')})` : ' (wolny)'}
              </option>
            );
          })}
      </TextField>
    </CorrectionDialog>
  );
};

/** Okno „Zmień naczepę” — numer rejestracyjny z kartoteki, pusty = bez naczepy. */
export const TrailerCorrectionDialog = ({
  open,
  onClose,
  plate,
  currentLabel,
  tmsLabel,
}: {
  open: boolean;
  onClose: () => void;
  plate: string;
  currentLabel: string;
  tmsLabel: string;
}) => {
  const state = useFleetCorrections();
  const notify = useNotify();
  const [value, setValue] = useState('');
  useEffect(() => {
    if (open) setValue('');
  }, [open]);
  const save = async (reason: string) => {
    if (state.carId === null) return;
    const result = await saveTrailerCorrection(state.carId, value, reason);
    notify(savedMessage(result, 'Zapisano korektę kartoteki'), { type: 'success' });
    onClose();
    state.reload();
  };
  return (
    <CorrectionDialog
      open={open}
      title={`Zmień naczepę: ${plate}`}
      intro={`Obecnie: ${currentLabel}. W TMS: ${tmsLabel}.`}
      okLabel="Zapisz korektę"
      canSave
      onClose={onClose}
      onSave={save}
      effects={
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          <li>
            <Typography variant="body2">{MARKED}</Typography>
          </li>
          <li>
            <Typography variant="body2">Ładowność zestawu w rekomendacjach liczy się z nowej naczepy.</Typography>
          </li>
        </Box>
      }
    >
      <TextField
        size="small"
        fullWidth
        label="Numer rejestracyjny naczepy"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        helperText="Naczepa z kartoteki pojazdów. Puste pole = bez naczepy."
        slotProps={{ htmlInput: { maxLength: 64, 'data-testid': 'car-correction-trailer-input' } }}
      />
    </CorrectionDialog>
  );
};

/** Okno „Zmień termin” uprawnienia pojazdu. */
export const EligibilityTermDialog = ({
  open,
  onClose,
  eligibilityId,
  name,
  current,
  tms,
}: {
  open: boolean;
  onClose: () => void;
  eligibilityId: number;
  name: string;
  /** Termin na karcie teraz (ISO). */
  current: string | null;
  /** Termin w TMS (ISO). */
  tms: string | null;
}) => {
  const state = useFleetCorrections();
  const notify = useNotify();
  const [date, setDate] = useState('');
  useEffect(() => {
    if (open) setDate(current ? current.slice(0, 10) : '');
  }, [open, current]);
  const save = async (reason: string) => {
    if (state.carId === null) return;
    const result = await saveEligibilityCorrection(state.carId, eligibilityId, date, reason);
    notify(savedMessage(result, 'Zapisano termin'), { type: 'success' });
    onClose();
    state.reload();
  };
  return (
    <CorrectionDialog
      open={open}
      title={`Zmień termin: ${name}`}
      intro={`Obecnie: ${termText(current)}. W TMS: ${termText(tms)}.`}
      okLabel="Zapisz termin"
      canSave={/^\d{4}-\d{2}-\d{2}$/.test(date)}
      onClose={onClose}
      onSave={save}
      effects={
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          <li>
            <Typography variant="body2">Termin będzie oznaczony jako różny od TMS.</Typography>
          </li>
          <li>
            <Typography variant="body2">
              Rekomendacje liczą z nowego terminu: nieważne uprawnienie typu Blokada wyklucza pojazd, Alert ostrzega.
            </Typography>
          </li>
        </Box>
      }
    >
      <TextField
        type="date"
        size="small"
        label="Nowy termin"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'car-correction-date' } }}
      />
    </CorrectionDialog>
  );
};

// ── sekcja „Kierowca i zestaw” ────────────────────────────────────────

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);

/** Sekcja karty pojazdu „Kierowca i zestaw” (tylko z przełącznikiem). */
export const CarDriverAndSet = () => {
  const r = useRecordContext<RaRecord & Record<string, unknown>>();
  const state = useFleetCorrections();
  const [dialog, setDialog] = useState<'driver' | 'trailer' | null>(null);
  if (!r || !state.enabled) return null;
  const corrections = correctionsOf(r);
  const driverFix = correctionFor(corrections, 'driver');
  const trailerFix = correctionFor(corrections, 'trailer');
  const driver = (r.driver ?? null) as { id: number; name?: unknown; surname?: unknown } | null;
  const coupled = (r.coupledVehicle ?? null) as { id: number; registrationNumber?: unknown } | null;
  const plate = text(r.registrationNumber) ?? text(r.name) ?? `#${String(r.id)}`;
  const driverName = personName(driver);
  const coupledPlate = coupled ? (text(coupled.registrationNumber) ?? `#${coupled.id}`) : null;
  const refusal = editRefusal(state);
  return (
    <SectionCard title="Kierowca i zestaw">
      <Box data-testid="car-corrections">
        <Table size="small">
          <TableBody>
            <Row label="Kierowca">
              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography
                    variant="body2"
                    data-testid="car-correction-driver-value"
                    color={driverName ? 'text.primary' : 'error.main'}
                  >
                    {driverName || 'brak'}
                  </Typography>
                  {driverFix ? <CorrectionMark correction={driverFix} testKey="driver" /> : null}
                  <CorrectionButton
                    label="Zmień"
                    testId="car-correction-edit-driver"
                    refusal={refusal}
                    onClick={() => setDialog('driver')}
                  />
                </Box>
                <TmsChanged correction={driverFix} testKey="driver" />
              </Stack>
            </Row>
            <Row label="Naczepa / drugi pojazd zestawu">
              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" component="span" data-testid="car-correction-trailer-value">
                    {coupled && coupledPlate ? <Link to={`/cars/${coupled.id}/show`}>{coupledPlate}</Link> : '—'}
                  </Typography>
                  {trailerFix ? <CorrectionMark correction={trailerFix} testKey="trailer" /> : null}
                  <CorrectionButton
                    label="Zmień"
                    testId="car-correction-edit-trailer"
                    refusal={refusal}
                    onClick={() => setDialog('trailer')}
                  />
                </Box>
                <TmsChanged correction={trailerFix} testKey="trailer" />
              </Stack>
            </Row>
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Zmiana kierowcy wpływa na rekomendacje. Przed zapisem zobaczysz warianty i obsadzenia, których dotyczy.
          {state.context?.access.note ? ` ${state.context.access.note}` : ''}
        </Typography>
      </Box>
      {/* Okna montowane dopiero po kliknięciu — bez zapytania o kierowców
          przy każdym otwarciu karty. */}
      {dialog === 'driver' ? (
        <DriverCorrectionDialog
          open
          onClose={() => setDialog(null)}
          plate={plate}
          current={driver}
          tmsLabel={driverFix ? driverFix.tmsLabel : driverName || 'bez kierowcy'}
        />
      ) : null}
      {dialog === 'trailer' ? (
        <TrailerCorrectionDialog
          open
          onClose={() => setDialog(null)}
          plate={plate}
          currentLabel={coupledPlate ?? 'bez naczepy'}
          tmsLabel={trailerFix ? trailerFix.tmsLabel : (coupledPlate ?? 'bez naczepy')}
        />
      ) : null}
    </SectionCard>
  );
};
